import { UserProcess } from '@interfaces';
import { Office, OfficeVisitDaySetting } from './office';
import { User } from './user-auth';

export enum AppointmentType {
  COMPANY_OFFICE_VISIT = 'COMPANY_OFFICE_VISIT',
  ONLINE_CONSULTATION = 'ONLINE_CONSULTATION',
}

export enum AppointmentStatus {
  // Foreigner requests an appointment date -> waiting for office(concultant) to accept or reject
  DATE_REQUESTED = 'DATE_REQUESTED',

  // Office accepts the requested date
  DATE_ACCEPTED = 'DATE_ACCEPTED',

  // Office rejects the requested date and provides suggested other dates
  // and provides comment
  DATE_REJECTED = 'DATE_REJECTED',

  // Foreigner or Office cancels the appointment
  // If office cancels - provides suggestions for other dates and gives comment
  APPOINTMENT_CANCELLED = 'APPOINTMENT_CANCELLED',

  // Appointment is completed (office marks it as completed after the appointment date)
  APPOINTMENT_COMPLETED = 'APPOINTMENT_COMPLETED',

  // Foreigner did not show up for the appointment and did not cancel it in advance.
  NO_SHOW = 'NO_SHOW',
}

export interface AppointmentExtraData {
  onlineMeetingLink?: string; // http link
  onlineMeetingId?: string; // simple code string
  onlineMeetingAccessCode?: string; // might be PIN might be password
  comment?: string; // Any comments consultant might have about the appointment
  [key: string]: any;
}

export interface Appointment {
  id: string;
  type: AppointmentType;
  status: AppointmentStatus;
  date: string; // ISO date string
  extraData: AppointmentExtraData;

  // Link to online meeting (videoconference) IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // This is required to be filled by the office consultant when accepting the appointment request, and will be provided to the foreigner.
  onlineMeetingLink?: string | null;
  // Meeting ID / PIN / Access Code for the online meeting IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // Completely optional, will be provided to the foreigner
  onlineMeetingExtraInfo?: string | null;

  // Comments provided by consultant at any point of appointment lifecycle, visible for foreigner
  commentForForeigner?: string | null;
  // INternal notes for other consultants that might work with this appointment. Not visible for foreigner.
  internalNotes?: string | null;

  office?: Partial<Office> | null;
  officeId: string;
  relatedUser?: Partial<User> | null;
  relatedUserId: string;
  relatedUserProcess?: Partial<UserProcess> | null;
  relatedUserProcessId: string | null;
  requestedAt?: string | null;

  acceptedAt?: string | null;
  acceptedBy?: Partial<User> | null;
  acceptedById?: string | null;

  rejectedAt?: string | null;
  rejectedBy?: Partial<User> | null;
  rejectedById?: string | null;

  dateSuggestions?: string[] | null; // ISO date strings array
  dateSuggestionsGivenBy?: Partial<User> | null;
  dateSuggestionsGivenById?: string | null;

  cancelledAt?: string | null;
  cancelledBy?: Partial<User> | null;
  cancelledById?: string | null;

  completedAt?: string | null;
  completedBy?: Partial<User> | null;
  completedById?: string | null;

  markedAsNoShowAt?: string | null;
  markedAsNoShowBy?: Partial<User> | null;
  markedAsNoShowById?: string | null;

  createdAt: string;
  updatedAt: string;

  // @TODO implement proper appointment history management/read
  history?: any[];
}

export interface RequestAppointmentForMyProcessOpts {
  relatedUserProcessId: string;
  type: AppointmentType;
  // ISO date string
  date: string;
}

export interface UpdateMyAppointmentDateOpts {
  appointmentId: string;
  date: string;
  type: AppointmentType;
  expectedCurrentDate: string;
}

export interface CreateAppointmentForUserProcessOpts {
  relatedUserProcessId: string;
  // ISO date string
  date: string;
  type: AppointmentType;
  status: AppointmentStatus;
  commentForForeigner?: string;
  internalNotes?: string;
  dateSuggestions?: string[];

  // Link to online meeting (videoconference) IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // This is required to be filled by the office consultant when accepting the appointment request, and will be provided to the foreigner.
  onlineMeetingLink?: string | null;
  // Meeting ID / PIN / Access Code for the online meeting IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // Completely optional, will be provided to the foreigner
  onlineMeetingExtraInfo?: string | null;
}

export interface UpdateAppointmentForUserProcessOpts {
  expectedDate: string;
  appointmentId: string;
  type?: AppointmentType;
  status?: AppointmentStatus;
  date?: string;
  dateSuggestions?: string[];

  // Link to online meeting (videoconference) IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // This is required to be filled by the office consultant when accepting the appointment request, and will be provided to the foreigner.
  onlineMeetingLink?: string | null;
  // Meeting ID / PIN / Access Code for the online meeting IF the AppointmentType is ONLINE_CONSULTATION. Otherwise null.
  // Completely optional, will be provided to the foreigner
  onlineMeetingExtraInfo?: string | null;

  commentForForeigner?: string;
  internalNotes?: string;
}

interface AppointmentActionBaseOpts {
  appointmentId: string;
  relatedUserProcessId: string;
  expectedDate: string;
  commentForForeigner?: string;
  onlineMeetingLink?: string | null;
  onlineMeetingExtraInfo?: string | null;
}

export interface AcceptAppointmentDateOpts extends AppointmentActionBaseOpts {}
export interface CancelAppointmentOpts extends AppointmentActionBaseOpts {}
export interface RejectAppointmentDateOpts extends AppointmentActionBaseOpts {
  dateSuggestions?: string[];
}
export interface MarkAppointmentAsOpts extends AppointmentActionBaseOpts {
  markAs: AppointmentStatus.APPOINTMENT_COMPLETED | AppointmentStatus.NO_SHOW;
}

export interface DateConstraintsForScheduling {
  nearestPossibleDate: string;
  requestingDateMaxDaysInFuture: number;
  cancellationMinHoursOfAdvance: number;
  reschedulingMinHoursOfAdvance: number;
  officeWeekSettings: OfficeVisitDaySetting[];
}
